/**
 * Funções de cálculo financeiro puras (sem estado).
 * Paridade com SimuladorMapa.aspx.cs (V1).
 */

// ==================== PRICE ====================

/**
 * PRICE: Calcula valor da parcela dado saldo e quantidade.
 * V1 ref: SimuladorMapa.aspx.cs linhas 407-475
 */
export function calcularValorParcelaPRICE(saldoFinanciar, taxa, qtdParcelas) {
  if (qtdParcelas <= 0) return 0;
  if (qtdParcelas === 1) return saldoFinanciar;

  if (taxa > 0) {
    var jurosPorParcela = Math.pow(1 + taxa, -qtdParcelas);
    return Math.round((saldoFinanciar / ((1 - jurosPorParcela) / taxa)) * 100) / 100;
  }
  return Math.round((saldoFinanciar / qtdParcelas) * 100) / 100;
}

/**
 * PRICE: Calcula quantidade de parcelas dado saldo e valor da parcela.
 * V1 ref: SimuladorMapa.aspx.cs linhas 477-559
 */
export function calcularQtdParcelasPRICE(saldoFinanciar, taxa, valorParcela) {
  if (valorParcela <= 0 || saldoFinanciar <= 0) return 0;

  var qtd;
  if (taxa > 0) {
    var denominador = (taxa * saldoFinanciar) / valorParcela;
    if (denominador >= 1) return 0;
    qtd = Math.log(1 / (1 - denominador)) / Math.log(1 + taxa);
  } else {
    qtd = saldoFinanciar / valorParcela;
  }
  return qtd;
}

/**
 * PRICE: Calcula entrada dado qtd parcelas e valor da parcela.
 * V1 ref: SimuladorMapa.aspx.cs linhas 580-648
 */
export function calcularEntradaPRICE(valorVista, taxa, qtdParcelas, valorParcela) {
  if (qtdParcelas <= 0 || valorParcela <= 0) return 0;

  if (taxa > 0) {
    var coeficiente = taxa / (1 - (1 / Math.pow(1 + taxa, qtdParcelas)));
    return Math.round((valorVista - (valorParcela / coeficiente)) * 100) / 100;
  }
  return Math.round((valorVista - (valorParcela * qtdParcelas)) * 100) / 100;
}

// ==================== SAC ====================

/**
 * Calcula valor de uma parcela SAC no mês i.
 * V1 ref: SimuladorMapa.aspx.cs linhas 397-405
 */
export function calcularParcelaSAC(amortizacao, qtdParcelas, i, taxa) {
  var saldoRemanescente = amortizacao * (qtdParcelas - i);
  var juros = saldoRemanescente * taxa;
  return Math.round((amortizacao + juros) * 100) / 100;
}

/**
 * SAC: Gera tabela completa de amortização.
 * V1 ref: SimuladorMapa.aspx.cs linhas 913-976
 */
export function gerarTabelaSAC(saldoDevedor, taxa, qtdParcelas) {
  if (qtdParcelas <= 0 || saldoDevedor <= 0) return [];

  var amortizacao = saldoDevedor / qtdParcelas;
  var tabela = [];
  var saldo = saldoDevedor;

  for (var i = 0; i < qtdParcelas; i++) {
    var juros = saldo * taxa;
    var prestacao = amortizacao + juros;
    saldo -= amortizacao;

    tabela.push({
      mes: i + 1,
      prestacao: Math.round(prestacao * 100) / 100,
      amortizacao: Math.round(amortizacao * 100) / 100,
      juros: Math.round(juros * 100) / 100,
      saldoDevedor: Math.round(Math.max(saldo, 0) * 100) / 100,
    });
  }
  return tabela;
}

/**
 * PRICE: Gera tabela completa de amortização.
 * Para cada mês: juros = saldo * taxa, amortizacao = valorParcela - juros,
 * saldo decresce por amortizacao.
 */
export function gerarTabelaPRICE(saldoDevedor, taxa, qtdParcelas, valorParcela) {
  if (qtdParcelas <= 0 || saldoDevedor <= 0 || valorParcela <= 0) return [];

  var tabela = [];
  var saldo = saldoDevedor;

  for (var i = 0; i < qtdParcelas; i++) {
    var juros = saldo * taxa;
    var amortizacao = valorParcela - juros;
    saldo -= amortizacao;

    tabela.push({
      mes: i + 1,
      prestacao: Math.round(valorParcela * 100) / 100,
      amortizacao: Math.round(amortizacao * 100) / 100,
      juros: Math.round(juros * 100) / 100,
      saldoDevedor: Math.round(Math.max(saldo, 0) * 100) / 100,
    });
  }
  return tabela;
}

/**
 * SAC: Calcula quantidade de parcelas dado valor da parcela.
 * V1 ref: SimuladorMapa.aspx.cs linhas 493-500
 */
export function calcularQtdParcelasSAC(saldoFinanciar, taxa, valorParcela) {
  if (valorParcela <= 0 || saldoFinanciar <= 0) return 0;

  var denominador = valorParcela - (saldoFinanciar * taxa);
  if (denominador <= 0) return 0;
  return saldoFinanciar / denominador;
}

// ==================== ANTECIPAÇÃO ====================

/**
 * Calcula valor presente de uma parcela futura.
 * V1 ref: SimuladorMapa.aspx.cs linhas 386-395
 */
export function calcularValorPresente(valorParcela, jurosPorFora, meses) {
  if (meses <= 0) return valorParcela;
  var dias = meses * 30;
  return Math.round((valorParcela / Math.pow(1 + jurosPorFora / 100, dias / 30)) * 100) / 100;
}

/**
 * Gera tabela completa de antecipação + resumos top 5/10/15.
 * V1 ref: SimuladorMapa.aspx.cs linhas 978-1077
 */
export function gerarTabelaAntecipacao(valorParcela, jurosPorFora, qtdParcelas, saldoFinanciar) {
  var parcelas = [];
  var totalTop5 = 0;
  var totalTop10 = 0;
  var totalTop15 = 0;

  for (var i = 0; i < qtdParcelas; i++) {
    var meses = i;
    var vp = calcularValorPresente(valorParcela, jurosPorFora, meses);
    var vpArredondado = Math.round(vp * 100) / 100;

    parcelas.push({
      numero: i + 1,
      valorOriginal: valorParcela,
      valorPresente: vpArredondado,
    });

    if (i >= qtdParcelas - 5) totalTop5 += vpArredondado;
    if (i >= qtdParcelas - 10) totalTop10 += vpArredondado;
    if (i >= qtdParcelas - 15) totalTop15 += vpArredondado;
  }

  return {
    parcelas: parcelas,
    top5: Math.round(totalTop5 * 100) / 100,
    top10: Math.round(totalTop10 * 100) / 100,
    top15: Math.round(totalTop15 * 100) / 100,
  };
}

/**
 * Calcula resumo de antecipação (Valor Original / Desconto / Valor Novo) para
 * um intervalo de parcelas. Direção determina o intervalo:
 * - "fim": últimas N parcelas (índices qtdTotal-N a qtdTotal-1)
 * - "inicio": primeiras N parcelas (índices 0 a N-1)
 *
 * Quando antecipa todas as parcelas e há saldoFinanciar, usa o saldo como Valor
 * Novo (mais preciso que o somatório de VPs por arredondamento).
 *
 * Retorna { valorOriginal, valorNovo, desconto, indiceInicio, indiceFim } —
 * onde indiceInicio/indiceFim são índices 0-based inclusivos para destacar
 * parcelas na tabela.
 */
export function calcularResumoAntecipacao(valorParcela, jurosPorFora, qtdTotal, qtdAntecipar, direcao, saldoFinanciar) {
  if (!qtdAntecipar || qtdAntecipar <= 0 || !qtdTotal) return null;

  const qtd = Math.min(qtdAntecipar, qtdTotal);
  const inicio = direcao === 'inicio' ? 0 : qtdTotal - qtd;
  const fim = inicio + qtd - 1;

  const valorOriginal = Math.round(qtd * valorParcela * 100) / 100;

  let valorNovo;
  if (qtd >= qtdTotal && saldoFinanciar) {
    valorNovo = Math.round(saldoFinanciar * 100) / 100;
  } else {
    let total = 0;
    for (let i = inicio; i <= fim; i++) {
      total += calcularValorPresente(valorParcela, jurosPorFora, i);
    }
    valorNovo = Math.round(total * 100) / 100;
  }

  const desconto = Math.round((valorOriginal - valorNovo) * 100) / 100;

  return {
    modo: 'qtd',
    qtd,
    direcao,
    valorOriginal,
    valorNovo,
    desconto,
    indiceInicio: inicio,
    indiceFim: fim,
  };
}

/**
 * W-0075 (devolução): antecipação por valor — informa quanto o cliente quer
 * antecipar (em R$) e mostra quantas parcelas cabem integralmente nesse valor,
 * a sobra e quanto falta pra incluir a próxima parcela.
 *
 * Direção: 'fim' (últimas — padrão de antecipação) ou 'inicio' (primeiras).
 * Retorna o mesmo formato do calcularResumoAntecipacao para reaproveitar a
 * UI (indiceInicio/indiceFim destacam parcelas na tabela).
 */
export function calcularResumoAntecipacaoPorValor(valorParcela, jurosPorFora, qtdTotal, valorDisponivel, direcao, saldoFinanciar) {
  if (!valorDisponivel || valorDisponivel <= 0 || !qtdTotal) return null;

  const dir = direcao === 'inicio' ? 'inicio' : 'fim';

  // Itera parcelas na ordem da direção, acumulando VP até passar do disponível.
  const vps = [];
  if (dir === 'inicio') {
    for (let i = 0; i < qtdTotal; i++) {
      vps.push(calcularValorPresente(valorParcela, jurosPorFora, i));
    }
  } else {
    for (let i = qtdTotal - 1; i >= 0; i--) {
      vps.push(calcularValorPresente(valorParcela, jurosPorFora, i));
    }
  }

  let acumulado = 0;
  let qtdQuitadas = 0;
  for (let i = 0; i < vps.length; i++) {
    const proximo = acumulado + vps[i];
    if (proximo > valorDisponivel + 1e-6) break;
    acumulado = proximo;
    qtdQuitadas++;
  }

  if (qtdQuitadas === 0) {
    // Nem a primeira parcela cabe — devolve essa info pro usuário entender.
    const faltaProxima = Math.round((vps[0] - valorDisponivel) * 100) / 100;
    return {
      modo: 'valor',
      direcao: dir,
      valorInformado: Math.round(valorDisponivel * 100) / 100,
      qtd: 0,
      qtdQuitadas: 0,
      sobra: Math.round(valorDisponivel * 100) / 100,
      faltaProxima: faltaProxima > 0 ? faltaProxima : 0,
      valorOriginal: 0,
      valorNovo: 0,
      desconto: 0,
      indiceInicio: -1,
      indiceFim: -1,
    };
  }

  const valorNovo = Math.round(acumulado * 100) / 100;
  const sobra = Math.round((valorDisponivel - acumulado) * 100) / 100;
  const faltaProxima =
    qtdQuitadas < qtdTotal
      ? Math.round((vps[qtdQuitadas] - sobra) * 100) / 100
      : 0;

  const valorOriginal = Math.round(qtdQuitadas * valorParcela * 100) / 100;
  const desconto = Math.round((valorOriginal - valorNovo) * 100) / 100;

  const inicio = dir === 'inicio' ? 0 : qtdTotal - qtdQuitadas;
  const fim = inicio + qtdQuitadas - 1;

  // Caso quitando tudo, usa saldoFinanciar quando disponível (mesma lógica do
  // calcularResumoAntecipacao para consistência de arredondamento).
  let valorNovoFinal = valorNovo;
  if (qtdQuitadas >= qtdTotal && saldoFinanciar) {
    valorNovoFinal = Math.round(saldoFinanciar * 100) / 100;
  }

  return {
    modo: 'valor',
    direcao: dir,
    valorInformado: Math.round(valorDisponivel * 100) / 100,
    qtd: qtdQuitadas,
    qtdQuitadas,
    sobra,
    faltaProxima,
    valorOriginal,
    valorNovo: valorNovoFinal,
    desconto,
    indiceInicio: inicio,
    indiceFim: fim,
  };
}

// ==================== ANTECIPAÇÃO UNIFICADA (W-0075 devolução pt.2) ====================
//
// Trabalha sobre uma LISTA HETEROGÊNEA de parcelas, ordenadas por vencimento.
// Cada parcela traz { numero, tipo, tipoLabel, valor (VF), valorPresente (VP),
// vencimento }. O VP de cada uma já foi calculado pelo chamador usando a taxa
// da própria origem (sinal=sem taxa→VP=VF, condicionantes/intermediárias=taxaPI,
// financiamento=juros_por_fora/taxa_financiamento).
//
// Vantagem: a função aqui não precisa saber NADA sobre taxas — só soma VPs.

/**
 * Tabela unificada de antecipação + sumário top 5/10/15 sobre uma lista de
 * parcelas heterogêneas já preparada pelo chamador.
 *
 * W-0075 (devolução SINAL pt.3): cada parcela pode vir com `antecipavel: false`
 * (caso de Sinal à vista — Depósito/PIX, Dinheiro, Cartão, Cheque). Essas ficam
 * no grid como informativo (linha verde no front), mas NÃO entram nos top
 * 5/10/15 nem nos cálculos por qtd/valor.
 */
export function gerarTabelaAntecipacaoUnificada(parcelas, saldoFinanciar) {
  var parcelasOut = (parcelas || []).map(function(p, i) {
    return {
      numero: i + 1,
      tipo: p.tipo,
      tipoLabel: p.tipoLabel || p.tipo,
      valorOriginal: Math.round((p.valor || 0) * 100) / 100,
      valorPresente: Math.round((p.valorPresente || 0) * 100) / 100,
      vencimento: p.vencimento,
      antecipavel: p.antecipavel !== false,
    };
  });
  // Top 5/10/15 — só somam parcelas antecipáveis, do FIM pra trás (parcelas
  // mais distantes têm maior VP descontado por estarem mais no futuro).
  var antecipaveis = parcelasOut.filter(function(p) { return p.antecipavel; });
  var nA = antecipaveis.length;
  var somaTop = function(qtd) {
    var ini = Math.max(0, nA - qtd);
    var t = 0;
    for (var i = ini; i < nA; i++) t += antecipaveis[i].valorPresente;
    return Math.round(t * 100) / 100;
  };
  return {
    parcelas: parcelasOut,
    top5: somaTop(5),
    top10: somaTop(10),
    top15: somaTop(15),
    saldoFinanciar: saldoFinanciar,
  };
}

/**
 * Antecipa N parcelas (do início ou do fim) da lista unificada.
 * Soma VPs e VFs do intervalo selecionado e devolve resumo.
 */
export function calcularResumoAntecipacaoUnificado(parcelas, qtdAntecipar, direcao, saldoFinanciar) {
  if (!qtdAntecipar || qtdAntecipar <= 0 || !parcelas || parcelas.length === 0) return null;

  // W-0075 (devolução SINAL pt.3): considera apenas índices antecipáveis.
  // Parcelas com `antecipavel === false` (Sinal à vista ≠ Boleto) entram só
  // como informativo no grid; o destaque visual pula sobre elas.
  var idxAntecipaveis = [];
  for (var idx = 0; idx < parcelas.length; idx++) {
    if (parcelas[idx].antecipavel !== false) idxAntecipaveis.push(idx);
  }
  if (idxAntecipaveis.length === 0) return null;

  var qtdTotalAntecipavel = idxAntecipaveis.length;
  var qtd = Math.min(qtdAntecipar, qtdTotalAntecipavel);
  // Pega N índices do início ou do fim da lista de antecipáveis.
  var fatia = direcao === 'inicio'
    ? idxAntecipaveis.slice(0, qtd)
    : idxAntecipaveis.slice(qtdTotalAntecipavel - qtd, qtdTotalAntecipavel);

  var valorOriginal = 0;
  var valorNovo = 0;
  for (var i = 0; i < fatia.length; i++) {
    var p = parcelas[fatia[i]];
    valorOriginal += p.valorOriginal || p.valor || 0;
    valorNovo += p.valorPresente || 0;
  }
  valorOriginal = Math.round(valorOriginal * 100) / 100;
  valorNovo = Math.round(valorNovo * 100) / 100;

  // Antecipou tudo (de antecipáveis) e tem saldoFinanciar oficial → prefere ele.
  if (qtd >= qtdTotalAntecipavel && saldoFinanciar) {
    valorNovo = Math.round(saldoFinanciar * 100) / 100;
  }

  var desconto = Math.round((valorOriginal - valorNovo) * 100) / 100;

  // indiceInicio/Fim mantêm a referência cronológica (ordem original) para o
  // destaque visual no PainelAntecipacao — usa primeiro e último da fatia.
  var indiceInicio = fatia[0];
  var indiceFim = fatia[fatia.length - 1];

  return {
    modo: 'qtd',
    qtd: qtd,
    direcao: direcao,
    valorOriginal: valorOriginal,
    valorNovo: valorNovo,
    desconto: desconto,
    indiceInicio: indiceInicio,
    indiceFim: indiceFim,
  };
}

/**
 * Antecipa por valor (R$) da lista unificada: itera parcelas na direção
 * escolhida acumulando VPs até passar do valor disponível.
 * Devolve qtdQuitadas + sobra + faltaProxima (igual à versão financing-only).
 */
export function calcularResumoAntecipacaoUnificadoPorValor(parcelas, valorDisponivel, direcao, saldoFinanciar) {
  if (!valorDisponivel || valorDisponivel <= 0 || !parcelas || parcelas.length === 0) return null;

  var dir = direcao === 'inicio' ? 'inicio' : 'fim';
  var qtdTotal = parcelas.length;

  // W-0075 (devolução SINAL pt.3): cria a sequência de iteração só com índices
  // antecipáveis. Sinais à vista (≠ Boleto) ficam fora — são apenas informativos
  // no grid.
  var ordemIdx = [];
  if (dir === 'inicio') {
    for (var i = 0; i < qtdTotal; i++) {
      if (parcelas[i].antecipavel !== false) ordemIdx.push(i);
    }
  } else {
    for (var j = qtdTotal - 1; j >= 0; j--) {
      if (parcelas[j].antecipavel !== false) ordemIdx.push(j);
    }
  }
  if (ordemIdx.length === 0) return null;

  var acumuladoVP = 0;
  var acumuladoVF = 0;
  var qtdQuitadas = 0;
  for (var k = 0; k < ordemIdx.length; k++) {
    var p = parcelas[ordemIdx[k]];
    var vp = p.valorPresente || 0;
    var proximo = acumuladoVP + vp;
    if (proximo > valorDisponivel + 1e-6) break;
    acumuladoVP = proximo;
    acumuladoVF += p.valorOriginal || p.valor || 0;
    qtdQuitadas++;
  }

  // Caso a primeira parcela já não caiba — devolve info de "falta".
  if (qtdQuitadas === 0) {
    var vpProx = parcelas[ordemIdx[0]].valorPresente || 0;
    var falta = Math.round((vpProx - valorDisponivel) * 100) / 100;
    return {
      modo: 'valor',
      direcao: dir,
      valorInformado: Math.round(valorDisponivel * 100) / 100,
      qtd: 0,
      qtdQuitadas: 0,
      sobra: Math.round(valorDisponivel * 100) / 100,
      faltaProxima: falta > 0 ? falta : 0,
      valorOriginal: 0,
      valorNovo: 0,
      desconto: 0,
      indiceInicio: -1,
      indiceFim: -1,
    };
  }

  var valorOriginal = Math.round(acumuladoVF * 100) / 100;
  var valorNovo = Math.round(acumuladoVP * 100) / 100;
  // Antecipou todas as parcelas ANTECIPÁVEIS (não conta o total bruto, pode haver
  // sinais à vista no meio) e há saldoFinanciar oficial → prefere ele.
  if (qtdQuitadas >= ordemIdx.length && saldoFinanciar) {
    valorNovo = Math.round(saldoFinanciar * 100) / 100;
  }
  var sobra = Math.round((valorDisponivel - acumuladoVP) * 100) / 100;
  var faltaProxima = 0;
  if (qtdQuitadas < ordemIdx.length) {
    var vpPx = parcelas[ordemIdx[qtdQuitadas]].valorPresente || 0;
    faltaProxima = Math.round((vpPx - sobra) * 100) / 100;
    if (faltaProxima < 0) faltaProxima = 0;
  }
  var desconto = Math.round((valorOriginal - valorNovo) * 100) / 100;

  // Para destaque visual: descobre o range [indiceInicio, indiceFim] na ordem
  // ORIGINAL (cronológica), não na de iteração. Pega o conjunto de índices.
  var idxSelecionados = ordemIdx.slice(0, qtdQuitadas);
  idxSelecionados.sort(function(a, b) { return a - b; });
  var indiceInicio = idxSelecionados[0];
  var indiceFim = idxSelecionados[idxSelecionados.length - 1];

  return {
    modo: 'valor',
    direcao: dir,
    valorInformado: Math.round(valorDisponivel * 100) / 100,
    qtd: qtdQuitadas,
    qtdQuitadas: qtdQuitadas,
    sobra: sobra,
    faltaProxima: faltaProxima,
    valorOriginal: valorOriginal,
    valorNovo: valorNovo,
    desconto: desconto,
    indiceInicio: indiceInicio,
    indiceFim: indiceFim,
  };
}

// ==================== CONDICIONANTES ====================

/**
 * Projeta parcelas condicionantes (Construção / Adimplência) a partir da
 * configuração de Intermediarias da família. Replica a lógica de
 * beginCalcularParcelasCondicionantes em Vendas/Financiamento/index.js.
 *
 * Cada item de config pode gerar 0, 1 ou 2 linhas:
 * - percentual_construcao > 0 → linha Construção (venc = dataVenda + meses_construcao, dia 20)
 * - percentual_adimplencia > 0 → linha Adimplência (venc = hoje + meses_adimplencia, dia 20)
 *
 * Valor presente = valorVenda × percentual/100.
 * Valor futuro = valorPresente corrigido pela taxa PI até o vencimento.
 */
export function projetarCondicionantes(valorVenda, intermediarias, dataVenda, taxaPI, calcularValorFuturo) {
  if (!intermediarias || intermediarias.length === 0 || !valorVenda) return [];

  var base = dataVenda ? new Date(dataVenda) : new Date();
  var hoje = new Date();
  var linhas = [];

  intermediarias.forEach(function(item) {
    var descricao = item.descricao;
    var pctConstrucao = Number(item.percentual || 0);
    var pctAdimplencia = Number(item.adimplencia_percentual || 0);

    if (pctConstrucao > 0) {
      var mesesC = Number(item.meses || 0);
      var vencC = new Date(base.getFullYear(), base.getMonth() + mesesC, 20);
      var vpC = Math.round(valorVenda * (pctConstrucao / 100) * 100) / 100;
      var vfC = calcularValorFuturo
        ? calcularValorFuturo(vpC, taxaPI, base, vencC)
        : vpC;

      linhas.push({
        codigo: item.codigo + '-c',
        codigoIntermediaria: item.codigo,
        tipo: 'Construção',
        descricao: descricao,
        meses: mesesC,
        percentual: pctConstrucao,
        valorPresente: vpC,
        valor: Math.round(vfC * 100) / 100,
        vencimento: vencC,
      });
    }

    if (pctAdimplencia > 0) {
      var mesesA = Number(item.adimplencia_meses || 0);
      var vencA = new Date(hoje.getFullYear(), hoje.getMonth() + mesesA, 20);
      var vpA = Math.round(valorVenda * (pctAdimplencia / 100) * 100) / 100;
      var vfA = calcularValorFuturo
        ? calcularValorFuturo(vpA, taxaPI, base, vencA)
        : vpA;

      linhas.push({
        codigo: item.codigo + '-a',
        codigoIntermediaria: item.codigo,
        tipo: 'Adimplência',
        descricao: descricao,
        meses: mesesA,
        percentual: pctAdimplencia,
        valorPresente: vpA,
        valor: Math.round(vfA * 100) / 100,
        vencimento: vencA,
      });
    }
  });

  return linhas;
}

// ==================== ENTRADA ====================

/**
 * Processa e valida entrada do cliente.
 * V1 ref: SimuladorMapa.aspx.cs CarregarEntrada
 */
export function processarEntrada(entradaCliente, entradaImobiliaria, entradaIncorporadora, valorVista) {
  if (entradaCliente < entradaImobiliaria) {
    return {
      valido: false,
      erro: 'Entrada do cliente deve ser maior ou igual à entrada imobiliária (R$ ' +
        entradaImobiliaria.toFixed(2).replace('.', ',') + ').',
    };
  }

  var saldoFinanciar = Math.round((valorVista - entradaCliente) * 100) / 100;
  if (saldoFinanciar < 0) saldoFinanciar = 0;

  return {
    valido: true,
    entradaCliente: entradaCliente,
    saldoFinanciar: saldoFinanciar,
  };
}

/**
 * Ajusta resto de quantidade de parcelas (modo Q).
 * Quando a divisão não é exata, ajusta a entrada para compensar.
 * V1 ref: SimuladorMapa.aspx.cs linhas 530-558
 */
export function ajustarRestoQtdParcelas(qtdCalculada, valorParcela, saldoFinanciar, entradaAtual, valorVista) {
  var qtdInteira = Math.floor(qtdCalculada);
  var resto = qtdCalculada - qtdInteira;

  if (resto <= 0) {
    return { qtdParcelas: qtdInteira, entradaAlterada: false, novaEntrada: entradaAtual };
  }

  var saldoResto = resto * valorParcela;
  var novaEntrada = entradaAtual + saldoResto;

  return {
    qtdParcelas: qtdInteira,
    entradaAlterada: true,
    novaEntrada: Math.round(novaEntrada * 100) / 100,
  };
}
